import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { BackChevron } from '../components/BackChevron';
import { ApiError, getProduct } from '../lib/api-client';
import { formatPrice, pickI18n } from '../lib/format';
import { useI18n } from '../lib/i18n-context';
import { sanitizeRichHtml } from '../lib/sanitize-html';

/**
 * 商品詳情頁。
 *
 * - `GET /products/:id` 取單一商品
 * - 顯示 i18n 名稱 / 描述 / 售價 / 時長（依目前 locale）
 * - 「選擇班次」→ `/products/:id/buy`
 */
export function ProductDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();

  const query = useQuery({
    queryKey: ['product', id],
    queryFn: () => {
      if (id === undefined) throw new Error('missing product id');
      return getProduct(id);
    },
    enabled: id !== undefined,
  });

  return (
    <div className="app-shell">
      <header className="topbar topbar-lead">
        <BackChevron to="/" label={t('common.back')} />
        <h1>{t('product.title')}</h1>
      </header>

      {query.isPending && <p>{t('common.loading')}</p>}
      {query.isError && (
        <p className="error-text">
          {t('product.loadFailed')}
          {query.error instanceof ApiError
            ? (query.error.problem?.detail ?? query.error.message)
            : t('common.unknownError')}
        </p>
      )}
      {query.isSuccess && (
        <article className="card">
          <h2 style={{ marginTop: 0 }}>{pickI18n(query.data.name_i18n, query.data.sku, locale)}</h2>
          {(() => {
            const desc = pickI18n(query.data.description_i18n, '', locale);
            return desc !== '' ? (
              <div
                style={{ color: '#555', lineHeight: 1.6 }}
                dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(desc) }}
              />
            ) : null;
          })()}
          <p>
            <strong style={{ fontSize: 24, color: 'var(--boat-primary)' }}>
              {formatPrice(query.data.base_price, query.data.currency, locale)}
            </strong>
            {query.data.duration_minutes !== null && (
              <span style={{ marginLeft: 8, color: '#777' }}>
                {t('product.durationLabel', { minutes: query.data.duration_minutes })}
              </span>
            )}
          </p>
          <p style={{ color: '#777', fontSize: 13 }}>
            {t('product.skuLabel')}
            {query.data.sku}
            <span style={{ marginLeft: 8 }}>
              {t('product.categoryLabel')}
              {query.data.category}
            </span>
          </p>
          <button
            type="button"
            className="btn-primary btn-block"
            onClick={() => navigate(`/products/${query.data.id}/buy`)}
            disabled={!query.data.is_published}
            style={{ marginTop: 12 }}
          >
            {query.data.is_published ? t('product.chooseSchedule') : t('product.unpublished')}
          </button>
        </article>
      )}
    </div>
  );
}
